import { Badge, Checkbox, Switch, Tag, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { concat, escapeRegExp, get, merge, sortBy } from "lodash";
import pluralize from "pluralize";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { Experience, Guide, Merchandise, PreferenceKey, ProductObject, Resource, Seller, Unit } from "types";
import { useDeletedExperiences } from "../../../hooks/resources/useDeletedExperiences";
import { useSeller } from "../../../hooks/useAccount";
import { useAutoFetchCollection } from "../../../hooks/useAutoFetchCollection";
import { useResource } from "../../../hooks/useResource";
import { SelectionState, useSelection } from "../../../hooks/useSelection";
import { LocalizationProps } from "../../../models/Locale";
import { findByKey } from "../../../models/Preference";
import { DashboardColorPreference, ResourceSelectionState } from "../../../types/Dashboard";
import { NoTranslate } from "../../Common/NoTranslate";
import { Loader } from "../../Loader";
import { MultiSelectPopover } from "../../MultiSelectPopover";
import { Color } from "../CustomizeDashboardModal";
import { useExperiences } from "@/hooks/resources/useProduct";

type Item = Experience | Resource | Guide | Unit | Merchandise;

const DefaultShow = { search: true, selectAll: true, unassignedCheckbox: false, sortOptionsBy: "name" };

export interface CriteriaItemProps extends LocalizationProps {
    label: string;
    title?: string;
    collection?: string[];
    value: SelectionState;
    defaultParams?: any;
    fetch?: (seller: Seller, collection: string[], params: any) => any;
    target?: (props: TagTargetProps) => JSX.Element;
    infoComponent?: () => JSX.Element;
    onChange: (selection: SelectionState, showUnassigned: boolean) => void;
    show?: {
        search?: boolean;
        selectAll?: boolean;
        unassignedCheckbox?: boolean;
        sortOptionsBy?: string | null;
        hideDeletedToggle?: boolean;
    };
    unassignedLabel?: string;
    hideDeletedToggleLabel?: string;
    hiddenItemBadgeLabel?: string;
    disableUnassignedOnSelection?: boolean;
    includeDeletedExperiences?: boolean; // Include deleted experiences to show in the dashboard product filter
    isItemDeleted?: (item: Item) => boolean;
    filterItem?: (item: Item) => boolean;
}

const useFetchCollection = (_seller: Seller, collection: string[], params: any) => {
    return useAutoFetchCollection<Item>(collection, params);
};

/**
 * TODO: This component is now doing a lot of things and it needs to be refactored into smaller pieces
 */
export const ListFilter = ({
    label,
    title,
    value,
    onChange,
    collection = [],
    defaultParams,
    localize = true,
    fetch = useFetchCollection,
    // A custom component as a child display of this criteria
    target: Target,
    infoComponent: InfoComponent,
    show = DefaultShow,
    unassignedLabel = "events",
    hideDeletedToggleLabel,
    hiddenItemBadgeLabel = "Deleted",
    disableUnassignedOnSelection = false,
    includeDeletedExperiences = false,
    isItemDeleted,
    filterItem,
}: CriteriaItemProps) => {
    const seller = useSeller();
    const [search, setSearch] = useState("");
    const [defaultSelection, setDefaultSelection] = useState<SelectionState>(value ?? { all: true, selected: [] });
    const [isShowUnassignedChecked, setIsShowUnassignedChecked] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [hideDeleted, setHideDeleted] = useState(false);
    const showFields = merge({}, DefaultShow, show);

    const [params, setParams] = useState<{ limit: number; "name[contains]": string | null }>(defaultParams);

    const { data = [], isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = fetch(seller, collection, params);

    const { deletedExperiences } = useDeletedExperiences(null, { enabled: includeDeletedExperiences });

    const items = useMemo(() => {
        return includeDeletedExperiences ? concat(data, deletedExperiences) : data;
    }, [data, deletedExperiences, includeDeletedExperiences]);

    // made this report for work only for experiences, because gift products arent considered in v1 as well.
    const experiences = useMemo(
        () => items.filter((item: { object: string }) => item.object !== ProductObject.GIFT_OFFER),
        [items],
    );
    const filteredItems = useMemo(() => {
        const searchRegex = search.length > 0 ? new RegExp(escapeRegExp(search), "i") : null;
        return experiences.filter((item: Item) => {
            const matchesSearch = !searchRegex || searchRegex.test(item.name);
            const isDeleted = isItemDeleted?.(item) ?? false;
            const shouldInclude = filterItem ? filterItem(item) : true;
            return shouldInclude && matchesSearch && (!hideDeleted || !isDeleted);
        });
    }, [experiences, search, hideDeleted, isItemDeleted, filterItem]);

    const isFiltered = search.length > 0 && filteredItems.length < items.length;
    const isSubset = isFiltered || (hasNextPage ?? false);
    const selection = useSelection(filteredItems, isSubset, value);

    useEffect(() => {
        const nextSelection = value ?? { all: true, selected: [] };
        setDefaultSelection(nextSelection);
        selection.setState(nextSelection);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleSearch = (text: string) => {
        setSearch(text.trim());

        if (hasNextPage) {
            // That means there is more data on the server.
            // Do the search on the server.
            setParams({ ...params, "name[contains]": text.length > 0 ? text : null });
        } else {
            setParams({ ...params, "name[contains]": null });
        }
    };

    const handleShowUnassignedChange = () => {
        setIsShowUnassignedChecked(!isShowUnassignedChecked);
    };

    const handleApply = () => {
        onChange(selection.state, isShowUnassignedChecked);
        setDefaultSelection(selection.state);
        setIsVisible(false);
        setSearch("");
    };

    const handleCancel = () => {
        setIsVisible(false);
        setSearch("");
    };

    const handleClose = () => {
        const newValue = { all: true, selected: [] };
        setDefaultSelection(newValue);
        onChange(newValue, true);
        setIsVisible(false);
        setSearch("");
    };

    const handleTagClick = () => {
        if (!isVisible) {
            // The menu is opened, update the selection state.
            selection.setState(value);
            setIsShowUnassignedChecked((value as ResourceSelectionState).showUnassigned ?? false);
        }

        setIsVisible(!isVisible);
    };

    const handleEndReached = () => {
        if (hasNextPage) {
            fetchNextPage();
        }
    };

    const TargetComponent = Target! ?? TagTarget;

    const defaultSelectionWithName = {
        ...defaultSelection,
        selected: defaultSelection.selected.map((id) => {
            const item = filteredItems?.find((item: Item) => item?.id === id);
            return { id, name: item?.name };
        }),
    };

    return (
        <MultiSelectPopover
            allowEmptySelection
            title={title}
            isVisible={isVisible}
            // Hack for now till we refactor the component
            placement={defaultParams?.type === "demographic" ? "top-start" : "bottom-start"}
            target={
                <TargetComponent
                    label={label}
                    collection={collection}
                    value={defaultSelectionWithName}
                    onClose={handleClose}
                    onClick={handleTagClick}
                />
            }
            showSearch={showFields.search}
            onApply={handleApply}
            onCancel={handleCancel}
            onSearch={handleSearch}
        >
            {!selection.isSelectedAll && InfoComponent && <InfoComponent />}

            {showFields.hideDeletedToggle && (
                <MultiSelectPopover.Header className="border-b border-b-gray-light">
                    <Switch.Group>
                        <Switch
                            disabled={!isItemDeleted}
                            isChecked={hideDeleted}
                            onChange={() => setHideDeleted((previous) => !previous)}
                        />
                        <Switch.Label direction="right">{hideDeletedToggleLabel}</Switch.Label>
                    </Switch.Group>
                </MultiSelectPopover.Header>
            )}

            {showFields.unassignedCheckbox ? (
                <MultiSelectPopover.Header className="border-b border-b-gray-light">
                    <FilterItem
                        label={"Show unassigned " + unassignedLabel}
                        isChecked={isShowUnassignedChecked}
                        isEnabled={disableUnassignedOnSelection ? selection.state.selected.length === 0 : true}
                        onChange={handleShowUnassignedChange}
                    />
                </MultiSelectPopover.Header>
            ) : null}

            {showFields.selectAll && (
                <SelectAllItem
                    isAllSelected={selection.state.all}
                    selectedCount={selection.count}
                    onChange={() => selection.selectAll()}
                />
            )}

            {filteredItems.length > 0 ? (
                <Virtuoso
                    style={{ height: 200 }}
                    data={showFields?.sortOptionsBy ? sortBy(filteredItems, showFields.sortOptionsBy) : filteredItems}
                    overscan={10}
                    endReached={handleEndReached}
                    itemContent={(_index, item) => {
                        const isDeleted = isItemDeleted?.(item) ?? false;
                        return (
                            <MultiSelectPopover.Item
                                key={item.id}
                                data-id={item.id}
                                className={clsx("border-t border-gray-light", { "no-translate": !localize })}
                            >
                                <FilterItem
                                    label={item.name}
                                    localize={localize}
                                    isEnabled={get(item, "enabled", true)}
                                    isChecked={selection.isSelected(item)}
                                    status={
                                        isDeleted ? (
                                            <Badge className="font-normal text-gray">{hiddenItemBadgeLabel}</Badge>
                                        ) : undefined
                                    }
                                    onChange={() => selection.select(item)}
                                />
                                {item.color && <Color className="mr-1 flex-shrink-0" value={item.color} size="small" />}
                            </MultiSelectPopover.Item>
                        );
                    }}
                    components={{
                        Footer: () => (isLoading || isFetchingNextPage ? <Loader /> : null),
                    }}
                />
            ) : (
                <div className="flex justify-center">
                    <span className="pb-4 pt-4 text-lg font-semibold text-black">No results found</span>
                </div>
            )}
        </MultiSelectPopover>
    );
};

export interface SelectionWithName {
    all: boolean;
    selected: Array<{ id: ID; name: string }>;
}

export interface TagTargetProps {
    label: string;
    collection: string[];
    value: SelectionWithName;
    onClose: () => void;
    onClick: () => void;
}

const TagTarget = ({ label, collection, value, onClose, onClick }: TagTargetProps) => {
    return (
        <Tag
            color="primary"
            size="medium"
            className={clsx("items-center leading-5 hover:cursor-pointer hover:border-black hover:bg-gray-light")}
            onClose={value.all ? undefined : onClose}
            onClick={onClick}
        >
            <TagContent label={label} collection={collection} selection={value} />
        </Tag>
    );
};

interface FilterItemProps extends LocalizationProps {
    label: string | ReactNode;
    isEnabled?: boolean;
    isChecked: boolean;
    onChange: (e: any) => void;
    status?: ReactNode;
    labelIcon?: ReactNode;
    className?: string;
    onClick?: () => void;
}

export const FilterItem = ({
    label,
    isEnabled = true,
    isChecked,
    onChange,
    status,
    labelIcon,
    className,
    onClick,
    localize = true,
}: FilterItemProps) => {
    const isDisabled = !isEnabled;

    return (
        <div
            className={clsx("flex w-full justify-between", isDisabled && "text-gray", className)}
            data-testid="filter-criteria-filter-item"
        >
            <div className="flex items-center gap-1">
                <Checkbox
                    classNames={{
                        label: clsx(
                            "flex justify-between w-full !leading-p1",
                            isDisabled ? "text-gray" : "!text-black",
                            {
                                "no-translate": !localize,
                            },
                        ),
                    }}
                    className="w-full"
                    disabled={isDisabled}
                    label={label}
                    checked={isChecked}
                    onChange={onChange}
                    onClick={onClick ? onClick : null}
                />
                {labelIcon}
            </div>
            {status}
        </div>
    );
};

interface TagContentProps {
    label: string;
    collection: string[];
    selection: SelectionWithName;
}

const TagContent = ({ label, collection, selection }: TagContentProps) => {
    const count = selection.selected.length;
    const isUnassignedOnly = (selection as any).showUnassigned === true && !selection.all && count === 0;

    if (count === 0 || selection.all) {
        // Use of `pluralize` is temporary, we should use LocalizeJS instead
        return (
            <span className="inline-flex items-center">
                <span className="mr-1 font-bold">{pluralize(label, 2)}:</span>
                {isUnassignedOnly ? "Unassigned" : "All"}
            </span>
        );
    }

    return (
        <Tooltip
            content={
                <div className="space-y-1 py-1">
                    {selection.selected.map((item) => (
                        <TagTooltipContent key={item.id} item={item} collection={collection} />
                    ))}
                </div>
            }
        >
            <span className="mr-1 font-bold">{pluralize(label, 2)}:</span>
            <span className="px-1">{count}</span>
        </Tooltip>
    );
};

interface SelectAllItemProps {
    isAllSelected: boolean;
    selectedCount: string | number;
    onChange: () => void;
}

export const SelectAllItem = ({ isAllSelected, selectedCount, onChange }: SelectAllItemProps) => {
    return (
        <MultiSelectPopover.Header>
            <FilterItem
                label={
                    <span className="contents space-x-2">
                        <span>Select All</span>
                        <Badge className="font-normal">
                            {isAllSelected ? (
                                <span>All Selected</span>
                            ) : (
                                <span>
                                    <NoTranslate>{selectedCount}</NoTranslate> Selected
                                </span>
                            )}
                        </Badge>
                    </span>
                }
                isChecked={isAllSelected}
                onChange={onChange}
            />
        </MultiSelectPopover.Header>
    );
};

interface TagTooltipContentProps {
    item: SelectionWithName["selected"][any];
    collection: string[];
}

const TagTooltipContent = ({ item, collection }: TagTooltipContentProps) => {
    const { data } = useResource<Item>([...collection, item.id], null, { enabled: !item.name });
    return (
        <NoTranslate as="div" data-item-id={item}>
            {item.name ?? data?.name ?? item.id}
        </NoTranslate>
    );
};

export const useProductFilterWithColor = () => {
    const { data: experiences } = useExperiences();

    const { data: preferences = [] } = useAutoFetchCollection<DashboardColorPreference>("preferences");

    const currentPreference = findByKey(preferences, PreferenceKey.XOLA_SELLER_DASHBOARD_COLORS);

    return experiences.map((experience) => {
        const value = { id: experience.id, name: experience.name, color: "" };
        if (currentPreference?.value?.experiences) {
            value.color = currentPreference.value.experiences[experience.id];
        }

        return value;
    });
};
